/**
 * O que faz: Tipos TypeScript para o sistema de análise diária de chats.
 * Onde usa: useChatAnalysis hook e componentes ChatAnalysisSection / PromptSuggestionCard.
 * Conexões: Tabelas chat_analysis_reports, prompt_improvement_suggestions, ai_analysis_spending.
 * Autor: IA
 * Data: 24/04/2026
 */

/** Uma camada de análise (hoje / 7 dias / período completo) */
export interface AnalysisLayer {
  periodo: 'hoje' | '7_dias' | 'completo';
  perguntas_frequentes: string[];
  queixas_recorrentes: string[];
  temas_detectados: string[];
  sentimento_geral: 'positivo' | 'neutro' | 'negativo' | 'misto';
  total_conversas: number;
  total_mensagens: number;
  resumo_narrativo: string;
}

/** Estrutura completa do campo `resultado` em chat_analysis_reports */
export interface ChatAnalysisResult {
  camadas: AnalysisLayer[];
}

/** Linha da tabela chat_analysis_reports */
export interface ChatAnalysisReport {
  id: string;
  clinica_id: string;
  periodo_inicio: string;
  periodo_fim: string;
  total_conversas: number;
  total_mensagens: number;
  resultado: ChatAnalysisResult;
  modelo_compressao: string;
  modelo_insights: string;
  tokens_compressao: number;
  tokens_insights: number;
  custo_estimado_usd: number;
  criado_em: string;
}

/** Linha da tabela prompt_improvement_suggestions */
export interface PromptImprovementSuggestion {
  id: string;
  clinica_id: string;
  analysis_report_id: string | null;
  prompt_atual: string | null;
  prompt_sugerido: string;
  mudancas_resumidas: string[];
  status: 'pendente' | 'aplicado' | 'ignorado';
  criado_em: string;
  resolvido_em: string | null;
}

/**
 * Configuração de análise de chats — campos da tabela clinicas.
 *
 * ⚙️ PONTO A — analise_modelo_compressao
 *    Modelo LLM para Stage 1 (compressão barata). Formato: 'provider:model_id'.
 *    Ex: 'openai:gpt-5-mini', 'anthropic:claude-3-5-haiku-20241022'
 *    Alterado em: ChatAnalysisSection → campo "Modelo de compressão"
 *
 * ⚙️ PONTO B — analise_modelo_insights
 *    Modelo LLM para Stage 2 (insights + sugestão de prompt). Pode ser mais capaz.
 *    Ex: 'openai:gpt-5.2', 'anthropic:claude-3-5-sonnet-20241022'
 *    Alterado em: ChatAnalysisSection → campo "Modelo de insights"
 *
 * ⚙️ PONTO C — analise_budget_mensal_usd
 *    Teto mensal em USD por clínica. Análises são puladas ao atingir o limite.
 *    Alterado em: ChatAnalysisSection → campo "Budget mensal (USD)"
 */
export interface ChatAnalysisConfig {
  analise_chat_ativo: boolean;
  analise_janela_dias: number;
  analise_ultima_execucao: string | null;
}

export const defaultChatAnalysisConfig: ChatAnalysisConfig = {
  analise_chat_ativo: false,
  analise_janela_dias: 30,
  analise_ultima_execucao: null,
};

export const PERIODO_LABELS: Record<AnalysisLayer['periodo'], string> = {
  hoje: 'Hoje',
  '7_dias': 'Últimos 7 dias',
  completo: 'Período completo',
};

export const SENTIMENTO_COLORS: Record<AnalysisLayer['sentimento_geral'], string> = {
  positivo: 'text-emerald-700 bg-emerald-50',
  neutro: 'text-gray-600 bg-gray-100',
  negativo: 'text-red-700 bg-red-50',
  misto: 'text-amber-700 bg-amber-50',
};
